// biome-ignore-all lint/suspicious/useIterableCallbackReturn: TODO - address later
// biome-ignore-all lint/correctness/useHookAtTopLevel: TODO - address later
// biome-ignore-all lint/performance/noAccumulatingSpread: TODO - address later
import { api } from "@/lib/api";
import type { MetricsFilters } from "@/lib/filters";

// Re-use the same metrics structure as campaign metrics
export interface ChannelMetrics {
  accounts_engaged?: number;
  total_engagements?: number;
  people_engaged?: number;
  engagements_per_account?: number;
  opportunities_influenced?: number;
  opportunities_influenced_amount?: number;
  opportunities_influenced_won?: number;
  opportunities_influenced_won_amount?: number;
  sourced_opportunities?: number;
  sourced_opportunities_amount?: number;
  opportunities_influenced_in_cycle?: number;
  opportunities_influenced_in_cycle_amount?: number;
  opportunities_created_post_engagement?: number;
  opportunities_created_post_engagement_amount?: number;
  progressed_opportunities?: number;
  progressed_opportunities_amount?: number;
  deal_contacts_engaged?: number;
  engagements_per_deal_contact?: number;
  accounts_reactivated?: number;
  accounts_activated?: number;
  accounts_without_opps?: number;
  days_since_first_engagement?: number;
  days_since_latest_engagement?: number;
  // Note: We don't include campaign_active_days for channels
  engagements_per_person?: number;
  first_engagement_date?: string;
  last_engagement_date?: string;
  opportunities_activated?: number;
  opportunities_activated_amount?: number;
  opportunities_activated_won?: number;
  opportunities_activated_won_amount?: number;
}

// Re-use the same drilldown types as campaign metrics
export interface AccountTouchedDrilldown {
  account_id: string;
  name: string;
  source_record_id: string;
  total_engagements: number;
  people_engaged: number;
  unique_people: number;
  first_engagement_date?: string;
  last_engagement_date?: string;
}

export interface TouchpointDrilldown {
  touchpoint_id: string;
  engagement_date: string;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  account_name: string;
}

export interface PersonTouchedDrilldown {
  person_id: string;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  job_title: string;
  account_name: string;
  total_engagements: number;
  first_engagement_date?: string;
  last_engagement_date?: string;
}

export interface TouchpointsPerAccountDrilldown {
  account_id: string;
  name: string;
  total_engagements: number;
  unique_people: number;
  engagements_per_person: number;
}

export interface InCycleOpportunityDrilldown {
  opportunity_id: string;
  name: string;
  stage_name: string;
  account_name: string;
  created_date: string;
  close_date: string;
  first_engagement_date: string;
  last_engagement_date: string;
  total_engagements: number;
  unique_people: number;
}

export interface ProgressedOpportunityDrilldown {
  opportunity_id: string;
  name: string;
  stage_name: string;
  account_name: string;
  stage_progression: string;
  created_date: string;
  progressed_date: string;
  total_engagements: number;
  total_amount: number;
}

export interface DealActorDrilldown {
  person_id: string;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  job_title: string;
  accounts: Array<{
    id: string;
    name: string;
    source_record_id: string;
  }>;
  opportunities: Array<{
    id: string;
    name: string;
    source_record_id: string;
    account_id: string;
  }>;
  total_engagements: number;
  first_engagement_date?: string;
  last_engagement_date?: string;
}

export interface TouchesPerDealActorDrilldown {
  person_id: string;
  account_id: string;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  job_title: string;
  account_name: string;
  total_engagements: number;
  opportunities_involved: number;
  engagements_per_opportunity: number;
}

export interface TimeSeriesDrilldown {
  id: string;
  date: string;
  engagement_count: number;
}

export type DrilldownData =
  | AccountTouchedDrilldown[]
  | TouchpointDrilldown[]
  | PersonTouchedDrilldown[]
  | TouchpointsPerAccountDrilldown[]
  | InCycleOpportunityDrilldown[]
  | ProgressedOpportunityDrilldown[]
  | DealActorDrilldown[]
  | TouchesPerDealActorDrilldown[]
  | TimeSeriesDrilldown[];

interface UseChannelMetricsOptions {
  channelType: string | null;
  metrics: (keyof ChannelMetrics)[];
  statuses?: string[];
  filters?: MetricsFilters;
  enabled?: boolean;
}

interface UseChannelMetricDrilldownOptions {
  channelType: string | null;
  metric: keyof ChannelMetrics;
  statuses?: string[];
  filters?: MetricsFilters;
  enabled?: boolean;
}

interface MetricLoadingState {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

interface UseChannelMetricsResult {
  data: ChannelMetrics;
  isLoading: boolean;
  loadingStates: Record<keyof ChannelMetrics, MetricLoadingState>;
}

// Map of which metrics are provided by which query groups
// Same as campaign metrics but with channel context
const METRIC_GROUPS = {
  basic_engagement: new Set([
    "accounts_engaged",
    "total_engagements",
    "people_engaged",
    "engagements_per_account",
    "engagements_per_person",
  ]),
  deal_metrics: new Set([
    "opportunities_influenced",
    "opportunities_influenced_amount",
    "opportunities_influenced_won",
    "opportunities_influenced_won_amount",
    "opportunities_influenced_in_cycle",
    "opportunities_influenced_in_cycle_amount",
    "opportunities_created_post_engagement",
    "opportunities_created_post_engagement_amount",
    "deal_contacts_engaged",
    "engagements_per_deal_contact",
  ]),
  sourced_opportunities: new Set([
    "sourced_opportunities",
    "sourced_opportunities_amount",
  ]),
  progressed_opportunities: new Set([
    "progressed_opportunities",
    "progressed_opportunities_amount",
  ]),
  account_activation: new Set([
    "accounts_reactivated",
    "accounts_activated",
    "accounts_without_opps",
  ]),
  response_timing: new Set([
    "days_since_first_engagement",
    "days_since_latest_engagement",
    "first_engagement_date",
    "last_engagement_date",
  ]),
  opportunities_activated: new Set([
    "opportunities_activated",
    "opportunities_activated_amount",
    "opportunities_activated_won",
    "opportunities_activated_won_amount",
  ]),
} as const;

// Map each metric to its group
const METRIC_TO_GROUP = Object.entries(METRIC_GROUPS).reduce(
  (acc, [group, metrics]) => {
    (metrics as Set<string>).forEach((metric) => {
      acc[metric] = group;
    });
    return acc;
  },
  {} as Record<string, string>,
);

/**
 * We split the metrics into groups based on which queries they need.
 * This allows us to fetch the metrics in parallel, and to update the loading states for each group independently.
 */
export function useChannelMetrics({
  channelType,
  metrics,
  statuses = [],
  filters = {},
  enabled = true,
}: UseChannelMetricsOptions): UseChannelMetricsResult {
  // Group metrics by their query group
  const metricsByGroup = metrics.reduce(
    (acc, metric) => {
      const group = METRIC_TO_GROUP[metric];
      if (group) {
        if (!acc[group]) {
          acc[group] = [];
        }
        acc[group].push(metric);
      }
      return acc;
    },
    {} as Record<string, Array<keyof ChannelMetrics>>,
  );

  // Create a query for each group that has metrics requested
  const queries = Object.entries(metricsByGroup).map(
    ([group, groupMetrics]) => {
      const searchParams = new URLSearchParams();
      groupMetrics.forEach((metric) => searchParams.append("metrics", metric));

      // Add statuses to query params if provided
      statuses.forEach((status) => searchParams.append("status", status));

      // Serialize filters object to JSON and add as a single parameter
      if (Object.keys(filters).length > 0) {
        searchParams.append("filters", JSON.stringify(filters));
      }

      return {
        group,
        metrics: groupMetrics,
        query: api.useQuery<ChannelMetrics>(
          `datahub/channels/${channelType || "null"}/metrics/?${searchParams}`,
          {
            enabled: enabled && !!channelType,
          },
        ),
      };
    },
  );

  // Initialize loading states for all requested metrics
  const loadingStates = metrics.reduce(
    (acc, metric) => {
      acc[metric] = {
        isLoading: true,
        isError: false,
        error: null,
      };
      return acc;
    },
    {} as Record<keyof ChannelMetrics, MetricLoadingState>,
  );

  // Update loading states and combine data from all queries
  const data = queries.reduce((acc, { metrics: groupMetrics, query }) => {
    // Update loading states for this group's metrics
    groupMetrics.forEach((metric) => {
      loadingStates[metric] = {
        isLoading: query.isLoading,
        isError: !!query.error,
        error: query.error,
      };
    });

    // Add this group's data if available
    if (query.data) {
      return { ...acc, ...query.data };
    }
    return acc;
  }, {} as ChannelMetrics);

  // Overall loading state is true only if all metrics are still loading
  const isLoading = Object.values(loadingStates).every(
    (state) => state.isLoading,
  );

  return {
    data,
    isLoading,
    loadingStates,
  };
}

export function useChannelMetricDrilldown({
  channelType,
  metric,
  statuses = [],
  filters = {},
  enabled = true,
}: UseChannelMetricDrilldownOptions) {
  const searchParams = new URLSearchParams();

  // Add statuses to query params if provided
  statuses.forEach((status) => searchParams.append("status", status));

  // Serialize filters object to JSON and add as a single parameter
  if (Object.keys(filters).length > 0) {
    searchParams.append("filters", JSON.stringify(filters));
  }

  const queryString = searchParams.toString()
    ? `?${searchParams.toString()}`
    : "";

  // Make sure we properly encode the channel name in the URL
  const encodedChannelType = channelType
    ? encodeURIComponent(channelType)
    : "null";

  return api.useQuery<DrilldownData>(
    `datahub/channels/${encodedChannelType}/metrics/${metric}/drilldown/${queryString}`,
    {
      // Enable the query if:
      // 1. The parent enabled flag is true
      // 2. We have a channel type
      enabled: enabled && !!channelType,
    },
  );
}

interface ChannelsResponse {
  results: Array<{
    id: string;
    name: string;
    type: string;
    description?: string;
    created_date?: string;
    last_updated?: string;
  }>;
}

export function useChannels() {
  return api.useQuery<ChannelsResponse>("datahub/channels/");
}

export function useChannelsByIds(channelIds?: string[]) {
  const enabled = channelIds && channelIds.length > 0;
  const queryString = enabled
    ? `?ids=${encodeURIComponent(channelIds?.join(","))}`
    : "";

  return api.useQuery<ChannelsResponse>(
    `datahub/channels/by-ids/${queryString}`,
    { enabled },
  );
}

interface ChannelStatusesResponse {
  statuses: Array<{
    status: string;
    represents_engagement: boolean;
  }>;
}

export function useChannelStatuses(channelType?: string) {
  return api.useQuery<ChannelStatusesResponse>(
    `datahub/channels/${channelType}/statuses/`,
    { enabled: !!channelType },
  );
}
